
import React from 'react';
import { useData } from '../../context/DataContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Truck, DollarSign, Users, Activity, Mail, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { trucks, inquiries, emailLogs } = useData();

  const totalValue = trucks.reduce((sum, t) => sum + t.price, 0);
  const availableTrucks = trucks.filter(t => t.available).length;
  const newInquiries = inquiries.filter(i => i.status === 'New').length;
  
  // Mock data for chart
  const trafficData = [
    { name: 'Mon', views: 400, leads: 4 },
    { name: 'Tue', views: 300, leads: 3 },
    { name: 'Wed', views: 200, leads: 8 },
    { name: 'Thu', views: 278, leads: 9 },
    { name: 'Fri', views: 189, leads: 8 },
    { name: 'Sat', views: 239, leads: 2 },
    { name: 'Sun', views: 349, leads: 5 },
  ];

  const StatCard = ({ title, value, icon, color, subtext }: any) => (
    <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 flex flex-col justify-between">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900 mt-2">{value}</h3>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
      {subtext && <div className="mt-4 text-xs text-gray-400">{subtext}</div>}
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-500">Welcome back, Admin. System status is healthy.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Inventory" 
          value={trucks.length} 
          icon={<Truck className="text-blue-600" size={24} />} 
          color="bg-blue-50"
          subtext={`${availableTrucks} Available for Sale`}
        />
        <StatCard 
          title="Inventory Value" 
          value={`$${(totalValue / 1000000).toFixed(1)}M`} 
          icon={<DollarSign className="text-green-600" size={24} />} 
          color="bg-green-50" 
          subtext="Total Asset Value"
        />
        <StatCard 
          title="Pending Inquiries" 
          value={newInquiries} 
          icon={<MessageSquare className="text-automax-red" size={24} />} 
          color="bg-red-50" 
          subtext="Requires Immediate Action"
        />
        <StatCard 
          title="Emails Sent" 
          value={emailLogs.length} 
          icon={<Mail className="text-purple-600" size={24} />} 
          color="bg-purple-50" 
          subtext="Campaign Activity"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-sm shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Traffic & Leads</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trafficData}>
                <defs>
                  <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D61F26" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#D61F26" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="views" stroke="#000" fillOpacity={1} fill="url(#colorViews)" />
                <Area type="monotone" dataKey="leads" stroke="#D61F26" fill="#D61F26" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Inquiries List */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-900">Recent Inquiries</h3>
            <Link to="/admin/inquiries" className="text-xs font-bold text-automax-red hover:underline uppercase">View All</Link>
          </div>
          <div className="space-y-4">
            {inquiries.slice(0, 5).map(inq => (
              <div key={inq.id} className="p-3 border-l-4 border-l-automax-red bg-gray-50 rounded-r-sm">
                <div className="flex justify-between items-start">
                  <h4 className="font-bold text-gray-900 text-sm">{inq.name}</h4>
                  <span className="text-xs text-gray-500">{new Date(inq.date).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1 line-clamp-1">{inq.message}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase bg-white px-2 py-0.5 rounded border border-gray-200">{inq.type}</span>
                </div>
              </div>
            ))}
            {inquiries.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No recent inquiries.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
